import { createSignal, createMemo, createEffect, Show } from 'solid-js';
import { login, karyawanLogin, brandCompany, brandCompanyName } from '../lib/session.js';
import { route, navigate } from '../lib/router.js';
import { APP_VERSION } from '../lib/version.js';

// Login — two separate identities, never conflated. The DEFAULT screen is the
// KARYAWAN portal (NIK + password); the admin (PENGGUNA) login lives at /admin.
// Mode follows the URL so each has a shareable address; the toggle navigates.
// The login page is branded with the logo of the company whoever last signed in
// here belonged to (remembered in localStorage).

// Once the admin login has been used on this device we keep the Karyawan/Admin
// switcher available — so an admin who taps "Karyawan" can get back to the admin
// login (important on the installed PWA, which has no address bar). A device that
// only ever uses the karyawan login never sets this, so the employee login stays
// clean. Persisted (not session-only) so it survives PWA relaunches.
const ADMIN_SEEN_KEY = 'payroll_admin_login';
const readAdminSeen = () => { try { return localStorage.getItem(ADMIN_SEEN_KEY) === '1'; } catch { return false; } };

export default function Login() {
  const mode = createMemo(() => (route() === '/admin' ? 'admin' : 'karyawan'));
  const [adminSeen, setAdminSeen] = createSignal(readAdminSeen());
  createEffect(() => {
    if (mode() === 'admin') {
      try { localStorage.setItem(ADMIN_SEEN_KEY, '1'); } catch { /* private mode */ }
      setAdminSeen(true);
    }
  });
  const [username, setUsername] = createSignal('');
  const [nik, setNik] = createSignal('');
  const [password, setPassword] = createSignal('');
  const [error, setError] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [logoBroken, setLogoBroken] = createSignal(false);
  const showLogo = () => brandCompany() && !logoBroken();

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      if (mode() === 'admin') {
        await login(username(), password());
        navigate('/dashboard');
      } else {
        await karyawanLogin(nik(), password());
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  // The toggle navigates so the URL always reflects which login is shown.
  function switchMode(m) {
    setError('');
    setPassword('');
    navigate(m === 'admin' ? '/admin' : '/');
  }

  return (
    <div class="login-wrap">
      <div class="login-brand">
        <Show when={showLogo()} fallback={<div class="brand-mark login-mark">MP</div>}>
          <img class="login-logo" src={`/api/company/${brandCompany()}/logo`}
            alt="logo perusahaan" onError={() => setLogoBroken(true)} />
        </Show>
        <h1>{brandCompanyName() || 'Metalindo Payroll'}</h1>
        <p>{mode() === 'admin' ? 'Portal Admin — masuk dengan akun pengguna' : 'Portal Karyawan — masuk dengan NIK'}</p>
      </div>

      {/* The switcher shows in admin mode AND on any device that has used the
          admin login before (adminSeen) — so an admin who toggles to Karyawan
          can get back. A karyawan-only device never sets adminSeen → stays clean. */}
      <Show when={adminSeen() || mode() === 'admin'}>
        <div class="seg login-seg">
          <button class="seg-item" classList={{ active: mode() === 'karyawan' }} onClick={() => switchMode('karyawan')}>Karyawan</button>
          <button class="seg-item" classList={{ active: mode() === 'admin' }} onClick={() => switchMode('admin')}>Admin</button>
        </div>
      </Show>

      <form class="card login-card" onSubmit={submit}>
        <Show when={mode() === 'admin'} fallback={
          <label class="kg-field">
            <span>NIK</span>
            <input type="text" autocomplete="username" autofocus
              placeholder="mis. B2M 100001"
              value={nik()} onInput={(e) => setNik(e.currentTarget.value)} />
          </label>
        }>
          <label class="kg-field">
            <span>Username</span>
            <input type="text" autocomplete="username" autofocus
              value={username()} onInput={(e) => setUsername(e.currentTarget.value)} />
          </label>
        </Show>
        <label class="kg-field">
          <span>Password</span>
          <input type="password" autocomplete="current-password"
            value={password()} onInput={(e) => setPassword(e.currentTarget.value)} />
        </label>
        <Show when={error()}>
          <div class="kg-verdict fail">{error()}</div>
        </Show>
        <button class="btn primary login-btn" type="submit" disabled={busy()}>
          {busy() ? 'Memeriksa…' : 'Masuk'}
        </button>
      </form>
      <p class="login-foot">
        {mode() === 'admin' ? 'Hanya untuk staf HR/keuangan.' : 'Slip gaji pribadi Anda. Rahasiakan password Anda.'}
      </p>
      <div class="app-version login-version">Payroll Metalindo · v{APP_VERSION}</div>
    </div>
  );
}
